import { getPreset } from "../../scene/presets";
import type {
  AnimationPreset,
  ElementAnimation,
  LoopAnimation,
  TimeRange,
} from "../../scene/schema";
import { defaultCaptionBox } from "../../video/frame";
import {
  type CaptionsNode,
  type DeviceNode,
  type IconNode,
  type ImageNode,
  type LogoNode,
  type ShapeNode,
  type StackNode,
  type TextNode,
  templateReferenceScale,
} from "../builder";

/**
 * Shorthands for authoring library templates. Coordinates refer to a 1080 px
 * wide reference page; videos to a shorter side of 1080 px (a 9:16 video is
 * 1080 × 1920, a 16:9 video 1920 × 1080).
 */

type Props<T> = Omit<T, "kind">;

export const text = (props: Props<TextNode>): TextNode => ({ kind: "text", ...props });
export const rect = (props: Omit<ShapeNode, "kind" | "shape">): ShapeNode => ({
  kind: "shape",
  shape: "rect",
  ...props,
});
export const ellipse = (props: Omit<ShapeNode, "kind" | "shape">): ShapeNode => ({
  kind: "shape",
  shape: "ellipse",
  ...props,
});
export const star = (props: Omit<ShapeNode, "kind" | "shape">): ShapeNode => ({
  kind: "shape",
  shape: "star",
  ...props,
});
export const line = (x: number, y: number, w: number, color: string, width = 3): ShapeNode => ({
  kind: "shape",
  shape: "line",
  x,
  y,
  w,
  h: width * 3,
  stroke: { color, width },
});
export const photo = (props: Props<ImageNode>): ImageNode => ({ kind: "image", ...props });
export const logo = (props: Props<LogoNode>): LogoNode => ({ kind: "logo", ...props });
export const stack = (props: Props<StackNode>): StackNode => ({ kind: "stack", ...props });
/** A device mockup with an empty screen; give `w` or `h`, the other side follows the device. */
export const device = (props: Props<DeviceNode>): DeviceNode => ({ kind: "device", ...props });
/** An icon of the built-in library (`BLOCK_ICONS`) in a brand colour. */
export const icon = (props: Props<IconNode>): IconNode => ({ kind: "icon", ...props });
export const captions = (props: Props<CaptionsNode>): CaptionsNode => ({
  kind: "captions",
  ...props,
});
/** Stack item: a text in a brand style. */
export const t = (
  value: string,
  style: TextNode["style"],
  size: number,
  color?: string,
  extra: Partial<Omit<TextNode, "kind" | "x" | "y" | "w">> = {},
): Omit<TextNode, "x" | "y" | "w"> => ({
  kind: "text",
  text: value,
  style,
  size,
  ...(color ? { color } : {}),
  ...extra,
});

/** A pill or button: shape plus centred label. */
export function pill(
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  fill: string,
  size = 30,
  extra: Partial<Pick<ShapeNode, "radius" | "rotation" | "animation" | "time">> = {},
): [ShapeNode, TextNode] {
  const { radius = "full", ...rest } = extra;
  return [
    rect({ x, y, w, h, fill, radius, name: label, ...rest }),
    text({
      text: label,
      style: "subline",
      size,
      color: `on:${fill.split("/")[0]}`,
      x,
      y,
      w,
      h,
      align: "center",
      ...rest,
    }),
  ];
}

// ---------------------------------------------------------------------------
// Video
// ---------------------------------------------------------------------------

/** Fixed colours for text straight on the video. */
export const WHITE = "#ffffff";
export const BLACK = "#000000";
/** Soft shadow that keeps white text readable on any picture. */
export const TEXT_SHADOW = { y: 4, blur: 22, color: "#000000a6" };

interface MotionOptions {
  duration?: number;
  /** Exit animation (only with a time range). Default: fade; `null`: none. */
  out?: AnimationPreset | null;
  outDuration?: number;
  loop?: LoopAnimation;
}

/**
 * Entrance of an element that stays until the video ends. Templates cannot
 * delay such elements (a start time needs an end, and the latest end would
 * set the length of the empty video); different durations stagger them.
 */
export function enter(
  entrance: AnimationPreset,
  options: Omit<MotionOptions, "out" | "outDuration"> = {},
): { animation: ElementAnimation } {
  return {
    animation: {
      in: { preset: entrance, duration: options.duration ?? 500 },
      ...(options.loop ? { loop: options.loop } : {}),
    },
  };
}

/** Visible from `start` to `end` (ms) with entrance and exit. */
export function during(
  start: number,
  end: number,
  entrance: AnimationPreset = "fade",
  options: MotionOptions = {},
): { time: TimeRange; animation: ElementAnimation } {
  const out = options.out === undefined ? "fade" : options.out;
  return {
    time: { start, end },
    animation: {
      in: { preset: entrance, duration: options.duration ?? 500 },
      ...(out ? { out: { preset: out, duration: options.outDuration ?? 350 } } : {}),
      ...(options.loop ? { loop: options.loop } : {}),
    },
  };
}

/**
 * Where a format expects captions (`defaultCaptionBox`), in reference px of
 * that format. Captions there move to the right place when the format changes.
 */
export function captionBox(presetId: string): { x: number; y: number; w: number; h: number } {
  const preset = getPreset(presetId);
  if (!preset) throw new Error(`Unbekanntes Format: ${presetId}`);
  const s = templateReferenceScale(preset);
  const box = defaultCaptionBox(preset);
  const r = (value: number) => Math.round((value / s) * 10) / 10;
  return { x: r(box.x), y: r(box.y), w: r(box.width), h: r(box.height) };
}

/** Caption box of 9:16 videos (x 76, y 1114, 928 × 307). */
export const REEL_CAPTIONS = captionBox("reel");

/** White text with a soft shadow, readable straight on the video. */
export const ON_VIDEO = { color: WHITE, shadow: TEXT_SHADOW };

/** Logo in the upper right corner, just below the app's top bar. */
export const cornerLogo = (on = BLACK) =>
  logo({ x: 780, y: 250, w: 230, h: 66, on, align: "right" });

/** Consecutive parts of a video (tips, steps, chapters) from `start`, `length` ms each. */
export const parts = (count: number, start: number, length: number) =>
  Array.from({ length: count }, (_, index) => ({
    index,
    start: start + index * length,
    end: start + (index + 1) * length,
  }));
